import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FiMessageSquare, FiMail, FiUpload, FiDownload, FiSearch, FiX, FiUser, FiUserPlus, FiPaperclip, FiChevronDown, FiChevronUp, FiMic } from 'react-icons/fi';
import { ImSpinner2 } from 'react-icons/im';
import { getContactsWithFilters, Contact, exportContactsForImport } from '../services/ContactService';
import {
    getEmailContactsPaged,
    importEmailContactsFromFile,
    downloadEmailContactTemplate,
    type EmailContactListDTO,
} from '../services/EmailContactService';
import { createEmailBroadcast, startEmailBroadcast } from '../services/EmailBroadcastService';
import { createBroadcast, startBroadcast } from '../services/BroadcastService';
import {
    createSparkCampaign,
    getSparkTemplate,
    listSparkTemplates,
    startSparkCampaign,
    type SparkMessageTemplateDetail,
    type SparkMessageTemplateSummary,
} from '../services/SparkCampaignService';
import { overLimitInfo } from '../utils/campaignOverLimit';
import { useSector } from '../contexts/SectorContext';
import SessionService from '../services/SessionService';
import Toast, { ToastContainer } from '../components/Toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { processAvatarUrl } from '../utils/avatarUtils';
import { apiErrorMessage, SESSION_EXPIRED_MESSAGE } from '../utils/apiErrorMessage';
import './ContactsPage.css';
import { LoadingSpinner } from '../components/LoadingSpinner';
import chatWebSocketService from '../services/ChatWebSocketService';
import { useModules } from '../contexts/ModuleContext';
import { isMendsVisionEnabled } from '../mendsAi';
import { openRegisterConversation } from '../mendsAi/openRegister';
import '../mendsAi/styles/mendsAi.css';

interface ToastMessage {
    id: number;
    message: string;
    type: 'success' | 'error' | 'info';
}

interface ContactImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImportSuccess: () => void;
    sectorId: number;
    isOfficial?: boolean;
}

const ContactImportModal: React.FC<ContactImportModalProps> = ({ isOpen, onClose, onImportSuccess, sectorId, isOfficial }) => {
    const [file, setFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successInfo, setSuccessInfo] = useState<string | null>(null);

    // Broadcast option
    const [enableBroadcast, setEnableBroadcast] = useState(false);
    const [broadcastMessages, setBroadcastMessages] = useState<string[]>(['']);
    const [broadcastName, setBroadcastName] = useState('');
    const [autoStart, setAutoStart] = useState(false);
    const [officialTemplates, setOfficialTemplates] = useState<SparkMessageTemplateSummary[]>([]);
    const [officialTemplateId, setOfficialTemplateId] = useState('');
    const [officialTemplateDetail, setOfficialTemplateDetail] = useState<SparkMessageTemplateDetail | null>(null);
    const [officialVars, setOfficialVars] = useState<Record<string, string>>({});

    const [focusedMessageIndex, setFocusedMessageIndex] = useState(0);
    const lastFocusedTextareaRef = useRef<HTMLTextAreaElement | null>(null);
    const PREVIEW_EXAMPLE_NAME = 'João';

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
            setError('Selecione um arquivo Excel válido (.xlsx ou .xls).');
            setFile(null);
            return;
        }

        setFile(selectedFile);
        setError(null);
    };

    const handleDownloadTemplate = async () => {
        try {
            const token = SessionService.getToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.mends.pro/api'}/Contact/template`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Não foi possível baixar o modelo.');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'contact_import_template.xlsx';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error(err);
            setError('Não foi possível baixar o modelo.');
        }
    };

    const handleAddMessage = () => {
        setBroadcastMessages(prev => [...prev, '']);
    };

    const handleRemoveMessage = (index: number) => {
        if (broadcastMessages.length <= 1) return;
        setBroadcastMessages(prev => prev.filter((_, i) => i !== index));
    };

    const handleMessageChange = (index: number, value: string) => {
        setBroadcastMessages(prev => prev.map((m, i) => i === index ? value : m));
    };

    const insertVariableAtCursor = (variable: string) => {
        const el = lastFocusedTextareaRef.current;
        const idx = focusedMessageIndex;
        if (el && idx >= 0 && idx < broadcastMessages.length) {
            const start = el.selectionStart;
            const end = el.selectionEnd;
            const text = broadcastMessages[idx];
            const newText = text.slice(0, start) + variable + text.slice(end);
            handleMessageChange(idx, newText);
            setTimeout(() => {
                el.focus();
                el.setSelectionRange(start + variable.length, start + variable.length);
            }, 0);
        } else {
            handleMessageChange(0, (broadcastMessages[0] || '') + variable);
        }
    };

    const previewMessage = (text: string) => text.replace(/\{name\}/g, PREVIEW_EXAMPLE_NAME);

    useEffect(() => {
        if (!isOpen || !isOfficial || !enableBroadcast || !sectorId) return;
        let cancelled = false;
        listSparkTemplates(sectorId)
            .then((tpls) => {
                if (!cancelled) setOfficialTemplates(tpls);
            })
            .catch(() => {
                if (!cancelled) setOfficialTemplates([]);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, isOfficial, enableBroadcast, sectorId]);

    useEffect(() => {
        if (!isOfficial || !officialTemplateId || !sectorId) {
            setOfficialTemplateDetail(null);
            return;
        }
        let cancelled = false;
        getSparkTemplate(sectorId, officialTemplateId)
            .then((d) => {
                if (cancelled) return;
                setOfficialTemplateDetail(d);
                const vars: Record<string, string> = {};
                for (const v of d.commonVariables || []) vars[v.key] = '';
                setOfficialVars(vars);
            })
            .catch(() => {
                if (!cancelled) setOfficialTemplateDetail(null);
            });
        return () => {
            cancelled = true;
        };
    }, [isOfficial, officialTemplateId, sectorId]);

    const handleImport = async () => {
        if (!file) {
            setError('Selecione um arquivo.');
            return;
        }

        // Validate broadcast if enabled
        if (enableBroadcast) {
            if (isOfficial) {
                if (!officialTemplateId) {
                    setError('Selecione um modelo de WhatsApp aprovado.');
                    return;
                }
                const missing = (officialTemplateDetail?.commonVariables || [])
                    .filter((v) => v.required && !(officialVars[v.key] || '').trim())
                    .map((v) => v.label || v.key);
                if (missing.length) {
                    setError(`Preencha as variáveis obrigatórias do modelo: ${missing.join(', ')}`);
                    return;
                }
            } else {
                const validMsgs = broadcastMessages.filter(m => m.trim() !== '');
                if (validMsgs.length === 0) {
                    setError('Adicione pelo menos uma mensagem para o disparo.');
                    return;
                }
            }
        }

        setIsImporting(true);
        setError(null);
        setSuccessInfo(null);

        try {
            const token = SessionService.getToken();
            if (!token) throw new Error(SESSION_EXPIRED_MESSAGE);

            const formData = new FormData();
            formData.append('file', file);
            formData.append('sectorId', sectorId.toString());
            if (enableBroadcast && !isOfficial) {
                formData.append('deferPersist', 'true');
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://server.mends.pro/api'}/Contact/import-file`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                const errorText = await response.text();
                let errorData: unknown = errorText;
                try {
                    errorData = JSON.parse(errorText);
                } catch {
                    /* corpo em texto puro */
                }
                throw Object.assign(new Error('Import failed'), { response: { status: response.status, data: errorData } });
            }

            const result = await response.json();
            const importedContacts = result?.Data || result?.data || [];
            const importCount = Array.isArray(importedContacts) ? importedContacts.length : 0;

            if (enableBroadcast && importCount > 0) {
                const contactsPayload = importedContacts
                    .map((c: any) => ({
                        id: 0,
                        name: (c.Name ?? c.name ?? c.Number ?? c.number ?? '').toString().trim() || (c.Number ?? c.number ?? '').toString(),
                        number: (c.Number ?? c.number ?? '').toString().trim(),
                    }))
                    .filter((c: { number: string }) => c.number && c.number.length >= 8);

                if (contactsPayload.length > 0) {
                    try {
                        if (isOfficial) {
                            const campaign = await createSparkCampaign(sectorId, {
                                name: broadcastName || `Campanha da importação ${new Date().toLocaleDateString('pt-BR')}`,
                                templateId: officialTemplateId,
                                platform: 'whatsapp',
                                recipients: contactsPayload.map((c: { name: string; number: string }) => ({
                                    phone: c.number,
                                    name: c.name,
                                })),
                                commonVariables: officialVars,
                                aiReplyEnabled: true,
                            });
                            if (campaign && autoStart) {
                                try {
                                    await startSparkCampaign(sectorId, campaign.id, officialVars);
                                    setSuccessInfo(`${importCount} contatos importados. Campanha oficial iniciada com ${contactsPayload.length} destinatários.`);
                                } catch (startError) {
                                    // Audiência acima do limite de mensagens do portfólio na Meta: fica como rascunho para confirmar em Campanhas.
                                    if (!overLimitInfo(startError)) throw startError;
                                    setSuccessInfo(`${importCount} contatos importados. A campanha ficou como rascunho: a audiência passa do limite de mensagens do seu portfólio na Meta. Abra Campanhas para confirmar o início.`);
                                }
                            } else if (campaign) {
                                setSuccessInfo(`${importCount} contatos importados. Campanha criada como rascunho: abra Campanhas para iniciar.`);
                            }
                        } else {
                            const validMsgs = broadcastMessages.filter(m => m.trim() !== '');
                            if (validMsgs.length > 0) {
                        const broadcast = await createBroadcast({
                            sector_id: sectorId,
                            name: broadcastName || `Disparo da importação ${new Date().toLocaleDateString('pt-BR')}`,
                            contact_ids: [],
                            contacts: contactsPayload,
                            messages: validMsgs,
                            recurrence_type: 1,
                            recurrence_interval: 1,
                            scheduled_time: '09:00',
                            exclude_weekends: false,
                            exclude_holidays: false,
                            materialize_contacts_on_send: true,
                            import_list_name: broadcastName || undefined,
                        });

                        if (broadcast && autoStart) {
                            const startResult = await startBroadcast(broadcast.id);
                            if (startResult.ok) {
                                setSuccessInfo(`${importCount} contatos importados. Disparo iniciado com ${contactsPayload.length} destinatários.`);
                            } else {
                                setSuccessInfo(`${importCount} contatos importados. Disparo criado, mas ainda não começou: abra Campanhas para iniciar.`);
                            }
                        } else if (broadcast) {
                            setSuccessInfo(`${importCount} contatos importados. Disparo criado e pendente: abra Campanhas para iniciar.`);
                        }
                            }
                        }
                    } catch (broadcastErr) {
                        console.error('Error creating broadcast:', broadcastErr);
                        setSuccessInfo(`${importCount} contatos importados, mas não foi possível criar a campanha.`);
                    }
                }
            }

            onImportSuccess();
            if (!enableBroadcast || importCount === 0) {
                onClose();
            }
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Não foi possível importar os contatos. Tente de novo.'));
        } finally {
            setIsImporting(false);
        }
    };

    const resetModal = () => {
        setFile(null);
        setError(null);
        setSuccessInfo(null);
        setEnableBroadcast(false);
        setBroadcastMessages(['']);
        setBroadcastName('');
        setAutoStart(false);
        setOfficialTemplates([]);
        setOfficialTemplateId('');
        setOfficialTemplateDetail(null);
        setOfficialVars({});
    };

    useEffect(() => {
        if (!isOpen) {
            resetModal();
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="contacts-modal-overlay" onClick={onClose}>
            <div className="contacts-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="contacts-modal-header">
                    <h2>Importar contatos</h2>
                    <button className="contacts-modal-close" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                <div className="contacts-modal-content">
                    {/* Download Template */}
                    <div className="contacts-import-section">
                        <h3>1. Baixe o modelo</h3>
                        <p className="contacts-import-description">
                            Baixe o modelo em Excel e preencha com os dados dos seus contatos.
                        </p>
                        <button className="contacts-template-button" onClick={handleDownloadTemplate}>
                            <FiDownload size={18} />
                            Baixar modelo em Excel
                        </button>
                    </div>

                    {/* Upload File */}
                    <div className="contacts-import-section">
                        <h3>2. Envie o arquivo</h3>
                        <p className="contacts-import-description">
                            Selecione o arquivo Excel com os seus contatos.
                            <br />
                            <small style={{ color: 'var(--error-color)', marginTop: '4px', display: 'block', fontWeight: 500 }}>
                                Atenção: no máximo 1000 contatos por arquivo. Números repetidos são ignorados automaticamente.
                            </small>
                        </p>
                        <label className="contacts-upload-area">
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <FiUpload size={24} />
                            <span>{file ? file.name : 'Clique para selecionar um arquivo'}</span>
                        </label>
                    </div>

                    {/* Broadcast Option */}
                    <div className="contacts-import-section">
                        <h3>3. Fazer um disparo (opcional)</h3>
                        <p className="contacts-import-description">
                            {isOfficial
                                ? 'Crie uma campanha oficial de WhatsApp com um modelo aprovado pela Meta para os contatos importados.'
                                : 'Envie uma mensagem para todos os contatos importados depois da importação.'}
                        </p>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                            <input
                                type="checkbox"
                                checked={enableBroadcast}
                                onChange={(e) => setEnableBroadcast(e.target.checked)}
                                style={{ width: 18, height: 18, accentColor: 'var(--primary-light)' }}
                            />
                            <span style={{ fontSize: 14, fontWeight: 500 }}>Fazer disparo depois da importação</span>
                        </label>

                        {enableBroadcast && (
                            <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
                                        Nome do disparo
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex.: Mensagem de boas-vindas"
                                        value={broadcastName}
                                        onChange={(e) => setBroadcastName(e.target.value)}
                                        style={{
                                            width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)',
                                            borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                                        }}
                                    />
                                </div>

                                {isOfficial ? (
                                    <>
                                        <div>
                                            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
                                                Modelo de WhatsApp aprovado <span style={{ color: 'var(--error-color)' }}>*</span>
                                            </label>
                                            <select
                                                value={officialTemplateId}
                                                onChange={(e) => setOfficialTemplateId(e.target.value)}
                                                style={{
                                                    width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)',
                                                    borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                                                }}
                                            >
                                                <option value="">Selecione o modelo…</option>
                                                {officialTemplates.map((t) => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {(officialTemplateDetail?.commonVariables || []).map((v) => (
                                            <div key={v.key}>
                                                <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
                                                    {v.label}{v.required ? ' *' : ''}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={officialVars[v.key] ?? ''}
                                                    onChange={(e) => setOfficialVars((prev) => ({ ...prev, [v.key]: e.target.value }))}
                                                    style={{
                                                        width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)',
                                                        borderRadius: 8, fontSize: 14, boxSizing: 'border-box',
                                                    }}
                                                />
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>
                                        Variações da mensagem <span style={{ color: 'var(--error-color)' }}>*</span>
                                    </label>
                                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 8px' }}>
                                        As mensagens se alternam entre os destinatários (1 mensagem por pessoa, em rodízio pela lista).
                                    </p>
                                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
                                        Variáveis da planilha (clique para inserir no texto):
                                    </p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                                        <button
                                            type="button"
                                            onClick={() => insertVariableAtCursor('{name}')}
                                            style={{
                                                padding: '6px 12px', borderRadius: 8, border: '1px solid var(--primary-color)',
                                                background: 'var(--bg-elevated)', color: 'var(--primary-light)', fontSize: 13, cursor: 'pointer',
                                                fontFamily: 'monospace',
                                            }}
                                        >
                                            {'{name}'}
                                        </button>
                                    </div>
                                    {broadcastMessages.map((msg, index) => (
                                        <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                                            <span style={{
                                                width: 24, height: 24, borderRadius: '50%', background: 'var(--primary-color)',
                                                color: 'var(--text-on-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: 11, fontWeight: 600, flexShrink: 0, marginTop: 6,
                                            }}>
                                                {index + 1}
                                            </span>
                                            <textarea
                                                onFocus={(e) => { lastFocusedTextareaRef.current = e.target as HTMLTextAreaElement; setFocusedMessageIndex(index); }}
                                                placeholder={`Variação ${index + 1} da mensagem…`}
                                                value={msg}
                                                onChange={(e) => handleMessageChange(index, e.target.value)}
                                                style={{
                                                    flex: 1, padding: '8px 12px', border: '1px solid var(--border-color)',
                                                    borderRadius: 8, fontSize: 14, minHeight: 50, resize: 'vertical',
                                                    fontFamily: 'inherit', boxSizing: 'border-box',
                                                }}
                                            />
                                            {broadcastMessages.length > 1 && (
                                                <button
                                                    onClick={() => handleRemoveMessage(index)}
                                                    style={{
                                                        background: 'none', border: 'none', color: 'var(--text-muted)',
                                                        cursor: 'pointer', fontSize: 18, marginTop: 6,
                                                    }}
                                                    title="Remover"
                                                    aria-label="Remover"
                                                >
                                                    ×
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                    {broadcastMessages.some(m => m.includes('{name}')) && (
                                        <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: 8 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>Prévia (exemplo com o nome &quot;{PREVIEW_EXAMPLE_NAME}&quot;):</div>
                                            {broadcastMessages.filter(m => m.trim() !== '').map((m, i) => (
                                                <div key={i} style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 4 }}>
                                                    {previewMessage(m)}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <button
                                        onClick={handleAddMessage}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                                            background: 'var(--bg-elevated)', border: '1px dashed var(--primary-color)', borderRadius: 8,
                                            color: 'var(--primary-light)', fontSize: 13, cursor: 'pointer',
                                        }}
                                    >
                                        + Adicionar variação da mensagem
                                    </button>
                                </div>
                                )}

                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={autoStart}
                                        onChange={(e) => setAutoStart(e.target.checked)}
                                        style={{ width: 16, height: 16, accentColor: 'var(--primary-light)' }}
                                    />
                                    <span style={{ fontSize: 13 }}>Iniciar o disparo logo depois da importação</span>
                                </label>
                            </div>
                        )}
                    </div>

                    {error && (
                        <div className="contacts-error-message">
                            {error}
                        </div>
                    )}

                    {successInfo && (
                        <div style={{
                            padding: '12px 16px', background: 'var(--success-bg)', borderRadius: 8,
                            color: 'var(--success-color)', fontSize: 14, marginTop: 8,
                        }}>
                            {successInfo}
                            <div style={{ marginTop: 8 }}>
                                <button
                                    onClick={onClose}
                                    style={{
                                        padding: '6px 16px', background: 'var(--primary-color)', color: 'var(--text-on-primary)',
                                        border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                                    }}
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {!successInfo && (
                    <div className="contacts-modal-footer">
                        <button className="contacts-cancel-button" onClick={onClose} disabled={isImporting}>
                            Cancelar
                        </button>
                        <button
                            className="contacts-import-button"
                            onClick={handleImport}
                            disabled={!file || isImporting}
                        >
                            {isImporting && <LoadingSpinner size="sm" color="white" />}
                            {enableBroadcast ? 'Importar e criar disparo' : 'Importar'}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

interface EmailContactImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    onImportSuccess: () => void;
    sectorId: number;
}

const PREVIEW_EXAMPLE_NAME_EMAIL = 'Joao';

const EmailContactImportModal: React.FC<EmailContactImportModalProps> = ({ isOpen, onClose, onImportSuccess, sectorId }) => {
    const [file, setFile] = useState<File | null>(null);
    const [isImporting, setIsImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successInfo, setSuccessInfo] = useState<string | null>(null);

    // Broadcast option (like WhatsApp import)
    const [enableBroadcast, setEnableBroadcast] = useState(false);
    const [broadcastName, setBroadcastName] = useState('');
    const [broadcastSubject, setBroadcastSubject] = useState('');
    const [broadcastBody, setBroadcastBody] = useState('');
    const [broadcastCc, setBroadcastCc] = useState('');
    const [broadcastBcc, setBroadcastBcc] = useState('');
    const [broadcastReplyTo, setBroadcastReplyTo] = useState('');
    const [broadcastAttachments, setBroadcastAttachments] = useState<Array<{ id: string; file: File }>>([]);
    const [showBroadcastCcBcc, setShowBroadcastCcBcc] = useState(false);
    const [autoStart, setAutoStart] = useState(false);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;
        if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
            setError('Selecione um arquivo Excel válido (.xlsx ou .xls).');
            setFile(null);
            return;
        }
        setFile(selectedFile);
        setError(null);
    };

    const handleDownloadTemplate = async () => {
        try {
            const blob = await downloadEmailContactTemplate();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'email_contact_import_template.xlsx';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error(err);
            setError('Não foi possível baixar o modelo.');
        }
    };

    const handleImport = async () => {
        if (!file) {
            setError('Selecione um arquivo.');
            return;
        }
        setIsImporting(true);
        setError(null);
        setSuccessInfo(null);
        try {
            const imported = await importEmailContactsFromFile(file, sectorId);
            setSuccessInfo(`${imported.length} ${imported.length === 1 ? 'contato de e-mail importado' : 'contatos de e-mail importados'}.`);
            onImportSuccess();

            if (enableBroadcast && imported.length > 0) {
                const emailContactIds = imported.map((c) => c.id);
                const attachments: Array<{ fileName: string; contentBase64: string; contentType: string }> = [];
                for (const a of broadcastAttachments) {
                    const base64 = await new Promise<string>((res, rej) => {
                        const r = new FileReader();
                        r.onload = () => res((r.result as string).split(',')[1] || '');
                        r.onerror = rej;
                        r.readAsDataURL(a.file);
                    });
                    attachments.push({ fileName: a.file.name, contentBase64: base64, contentType: a.file.type || 'application/octet-stream' });
                }
                const dto = {
                    sectorId,
                    name: broadcastName || `Disparo ${new Date().toLocaleString('pt-BR')}`,
                    subject: broadcastSubject,
                    bodyHtml: broadcastBody,
                    emailContactIds,
                    cc: broadcastCc.trim() || undefined,
                    bcc: broadcastBcc.trim() || undefined,
                    replyTo: broadcastReplyTo.trim() || undefined,
                    attachments: attachments.length > 0 ? attachments : undefined,
                };
                const created = await createEmailBroadcast(dto);
                if (autoStart) {
                    await startEmailBroadcast(created.id);
                }
                setSuccessInfo((prev) =>
                    prev + (autoStart ? ' Disparo criado e iniciado.' : ' Disparo criado.')
                );
            }
        } catch (err: any) {
            setError(apiErrorMessage(err, 'Não foi possível importar os contatos de e-mail. Tente de novo.'));
        } finally {
            setIsImporting(false);
        }
    };

    const resetModal = () => {
        setFile(null);
        setError(null);
        setSuccessInfo(null);
        setEnableBroadcast(false);
        setBroadcastName('');
        setBroadcastSubject('');
        setBroadcastBody('');
        setBroadcastCc('');
        setBroadcastBcc('');
        setBroadcastReplyTo('');
        setBroadcastAttachments([]);
        setShowBroadcastCcBcc(false);
        setAutoStart(false);
    };

    useEffect(() => {
        if (!isOpen) resetModal();
    }, [isOpen]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="contacts-modal-overlay" onClick={onClose}>
            <div className="contacts-modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                <div className="contacts-modal-header">
                    <h2>Importar contatos de e-mail</h2>
                    <button className="contacts-modal-close" onClick={onClose}>
                        <FiX />
                    </button>
                </div>
                <div className="contacts-modal-content">
                    <div className="contacts-import-section">
                        <h3>1. Baixe o modelo</h3>
                        <p className="contacts-import-description">
                            Baixe o modelo em Excel e preencha as colunas Nome e E-mail.
                        </p>
                        <button className="contacts-template-button" onClick={handleDownloadTemplate}>
                            <FiDownload size={18} />
                            Baixar modelo em Excel
                        </button>
                    </div>
                    <div className="contacts-import-section">
                        <h3>2. Envie o arquivo</h3>
                        <p className="contacts-import-description">
                            Selecione o arquivo Excel com os seus contatos de e-mail.
                            <br />
                            <small style={{ color: 'var(--error-color)', marginTop: '4px', display: 'block', fontWeight: 500 }}>
                                No máximo 1000 contatos por arquivo. E-mails repetidos são ignorados automaticamente.
                            </small>
                        </p>
                        <label className="contacts-upload-area">
                            <input
                                type="file"
                                accept=".xlsx,.xls"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <FiUpload size={24} />
                            <span>{file ? file.name : 'Clique para selecionar um arquivo'}</span>
                        </label>
                    </div>

                    {/* Broadcast Option */}
                    <div className="contacts-import-section">
                        <h3>3. Fazer um disparo (opcional)</h3>
                        <p className="contacts-import-description">
                            Envie um disparo de e-mail para todos os contatos importados depois da importação.
                        </p>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 12 }}>
                            <input
                                type="checkbox"
                                checked={enableBroadcast}
                                onChange={(e) => setEnableBroadcast(e.target.checked)}
                                style={{ width: 18, height: 18, accentColor: 'var(--primary-light)' }}
                            />
                            <span style={{ fontSize: 14, fontWeight: 500 }}>Fazer disparo depois da importação</span>
                        </label>

                        {enableBroadcast && (
                            <div style={{ padding: '12px 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>Nome do disparo</label>
                                    <input
                                        type="text"
                                        placeholder="Ex.: E-mail de boas-vindas"
                                        value={broadcastName}
                                        onChange={(e) => setBroadcastName(e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>Assunto <span style={{ color: 'var(--error-color)' }}>*</span></label>
                                    <input
                                        type="text"
                                        placeholder="Assunto do e-mail"
                                        value={broadcastSubject}
                                        onChange={(e) => setBroadcastSubject(e.target.value)}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)' }}>Corpo <span style={{ color: 'var(--error-color)' }}>*</span></label>
                                    <div className="form-hint" style={{ marginBottom: 6 }}>
                                        Escreva o conteúdo do e-mail em texto simples. As variáveis são trocadas para cada destinatário.
                                    </div>
                                    <div className="form-hint" style={{ marginBottom: 8, fontSize: 12 }}>
                                        Variáveis (clique para inserir no texto):
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
                                        <button type="button" onClick={() => setBroadcastBody((prev) => prev + '{name}')} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--primary-color)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'monospace' }}>{'{name}'}</button>
                                        <button type="button" onClick={() => setBroadcastBody((prev) => prev + '{email}')} style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--primary-color)', background: 'var(--bg-elevated)', color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer', fontFamily: 'monospace' }}>{'{email}'}</button>
                                    </div>
                                    <textarea
                                        placeholder="Olá, {name}!\n\nEscreva o conteúdo em texto simples.\n\nAbraços"
                                        value={broadcastBody}
                                        onChange={(e) => setBroadcastBody(e.target.value)}
                                        rows={5}
                                        style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 14, resize: 'vertical', boxSizing: 'border-box' }}
                                    />
                                    {(broadcastBody.includes('{name}') || broadcastBody.includes('{email}')) && (
                                        <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: 8 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4 }}>Prévia (exemplo: nome &quot;{PREVIEW_EXAMPLE_NAME_EMAIL}&quot;, e-mail &quot;{PREVIEW_EXAMPLE_NAME_EMAIL.toLowerCase()}@example.com&quot;):</div>
                                            <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{broadcastBody.replace(/\{name\}/g, PREVIEW_EXAMPLE_NAME_EMAIL).replace(/\{email\}/g, `${PREVIEW_EXAMPLE_NAME_EMAIL.toLowerCase()}@example.com`)}</div>
                                        </div>
                                    )}
                                </div>

                                {/* Cc, Cco e responder para */}
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setShowBroadcastCcBcc(!showBroadcastCcBcc)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
                                            color: 'var(--primary-light)', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: 0,
                                        }}
                                    >
                                        {showBroadcastCcBcc ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                                        Cc, Cco e responder para
                                    </button>
                                    {showBroadcastCcBcc && (
                                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12, padding: 12, background: 'var(--bg-elevated)', borderRadius: 8, border: '1px solid var(--border-color)' }}>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-tertiary)' }}>Cc</label>
                                                <input type="text" value={broadcastCc} onChange={e => setBroadcastCc(e.target.value)} placeholder="email1@example.com, email2@example.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-tertiary)' }}>Cco</label>
                                                <input type="text" value={broadcastBcc} onChange={e => setBroadcastBcc(e.target.value)} placeholder="email1@example.com, email2@example.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                                            </div>
                                            <div>
                                                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4, color: 'var(--text-tertiary)' }}>Responder para</label>
                                                <input type="email" value={broadcastReplyTo} onChange={e => setBroadcastReplyTo(e.target.value)} placeholder="reply@example.com" style={{ width: '100%', padding: '8px 12px', border: '1px solid var(--border-color)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Attachments */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                                    <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-primary)', width: '100%', textAlign: 'center' }}>Anexos</label>
                                    <input
                                        type="file"
                                        multiple
                                        accept="*/*"
                                        onChange={(e) => {
                                            const files = e.target.files;
                                            if (!files?.length) return;
                                            const maxSize = 5 * 1024 * 1024;
                                            const maxTotal = 15 * 1024 * 1024;
                                            const currentTotal = broadcastAttachments.reduce((s, a) => s + a.file.size, 0);
                                            const toAdd: Array<{ id: string; file: File }> = [];
                                            let newTotal = currentTotal;
                                            for (let i = 0; i < files.length; i++) {
                                                if (files[i].size > maxSize) continue;
                                                if (newTotal + files[i].size > maxTotal) break;
                                                toAdd.push({ id: `${Date.now()}-${i}`, file: files[i] });
                                                newTotal += files[i].size;
                                            }
                                            setBroadcastAttachments(prev => [...prev, ...toAdd]);
                                            e.target.value = '';
                                        }}
                                        style={{ display: 'none' }}
                                        id="email-import-attachments-input"
                                    />
                                    <label
                                        htmlFor="email-import-attachments-input"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '8px 16px',
                                            border: '1px dashed var(--border-strong)', borderRadius: 8, background: 'var(--bg-elevated)',
                                            cursor: 'pointer', fontSize: 13, color: 'var(--text-tertiary)', width: '100%',
                                        }}
                                    >
                                        <FiPaperclip size={16} />
                                        Adicionar arquivos (até 5 MB cada, 15 MB no total)
                                    </label>
                                    {broadcastAttachments.length > 0 && (
                                        <ul style={{ marginTop: 8, padding: 0, listStyle: 'none', width: '100%' }}>
                                            {broadcastAttachments.map((a) => (
                                                <li key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
                                                    <FiPaperclip size={12} style={{ color: 'var(--text-muted)' }} />
                                                    <span style={{ flex: 1 }}>{a.file.name}</span>
                                                    <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{(a.file.size / 1024).toFixed(1)} KB</span>
                                                    <button type="button" onClick={() => setBroadcastAttachments(prev => prev.filter(x => x.id !== a.id))} style={{ background: 'none', border: 'none', color: 'var(--error-color)', cursor: 'pointer', padding: 4 }} title="Remover" aria-label="Remover anexo">×</button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={autoStart}
                                        onChange={(e) => setAutoStart(e.target.checked)}
                                        style={{ width: 16, height: 16, accentColor: 'var(--primary-light)' }}
                                    />
                                    <span style={{ fontSize: 13 }}>Iniciar o disparo logo depois da importação</span>
                                </label>
                            </div>
                        )}
                    </div>

                    {error && <div className="contacts-error-message">{error}</div>}
                    {successInfo && (
                        <div style={{
                            padding: '12px 16px', background: 'var(--success-bg)', borderRadius: 8,
                            color: 'var(--success-color)', fontSize: 14, marginTop: 8,
                        }}>
                            {successInfo}
                            <div style={{ marginTop: 8 }}>
                                <button
                                    onClick={onClose}
                                    style={{
                                        padding: '6px 16px', background: 'var(--primary-color)', color: 'var(--text-on-primary)',
                                        border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                                    }}
                                >
                                    Fechar
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {!successInfo && (
                    <div className="contacts-modal-footer">
                        <button className="contacts-cancel-button" onClick={onClose} disabled={isImporting}>Cancelar</button>
                        <button
                            className="contacts-import-button"
                            onClick={handleImport}
                            disabled={
                                !file ||
                                isImporting ||
                                (enableBroadcast && (!broadcastSubject.trim() || !broadcastBody.trim()))
                            }
                        >
                            {isImporting && <LoadingSpinner size="sm" color="white" />}
                            {enableBroadcast ? 'Importar e criar disparo' : 'Importar'}
                        </button>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
};

const PAGE_SIZE = 50;

type ContactTab = 'whatsapp' | 'email';

const ContactsPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const tabFromUrl = searchParams.get('tab');
    const [activeTab, setActiveTab] = useState<ContactTab>(tabFromUrl === 'email' ? 'email' : 'whatsapp');
    const { hasModule } = useModules();
    const { isOfficial } = useSector();

    useEffect(() => {
        if (tabFromUrl === 'email') setActiveTab('email');
    }, [tabFromUrl]);

    useEffect(() => {
        if (activeTab === 'email' && !hasModule('email')) setActiveTab('whatsapp');
    }, [activeTab, hasModule]);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
    const [emailContacts, setEmailContacts] = useState<EmailContactListDTO[]>([]);
    const [emailTotalCount, setEmailTotalCount] = useState(0);
    const [emailPage, setEmailPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isLoadingEmail, setIsLoadingEmail] = useState(true);
    const [totalFromServer, setTotalFromServer] = useState<number | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [nextOffset, setNextOffset] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [emailSearchTerm, setEmailSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showEmailImportModal, setShowEmailImportModal] = useState(false);
    const [isExportingContacts, setIsExportingContacts] = useState(false);
    const [statusFilter, setStatusFilter] = useState<string>('todos');
    const [emailArchivedFilter, setEmailArchivedFilter] = useState<boolean | undefined>(undefined);
    const navigate = useNavigate();
    const visionEnabled = isMendsVisionEnabled();

    const CONTACTS_PER_PAGE = 15;

    const fetchContacts = useCallback(async () => {
        const sectorId = SessionService.getSectorId();
        if (!sectorId) {
            setIsLoading(false);
            return;
        }

        try {
            setIsLoading(true);
            const response = await getContactsWithFilters(sectorId, false, false, PAGE_SIZE, 0);
            const individualContacts = (response.data || []).filter((c: Contact) => !c.number?.includes('@g.us'));
            setContacts(individualContacts);
            setFilteredContacts(individualContacts);
            const pag = response.pagination;
            setTotalFromServer(pag?.total ?? individualContacts.length);
            setHasMore(pag?.hasMore ?? false);
            setNextOffset(pag?.nextOffset ?? PAGE_SIZE);
        } catch (error) {
            console.error('Error fetching contacts:', error);
            addToast('Não foi possível carregar os contatos.', 'error');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadMoreContacts = useCallback(async () => {
        const sectorId = SessionService.getSectorId();
        if (!sectorId || !hasMore || isLoadingMore) return;
        try {
            setIsLoadingMore(true);
            const response = await getContactsWithFilters(sectorId, false, false, PAGE_SIZE, nextOffset);
            const individualContacts = (response.data || []).filter((c: Contact) => !c.number?.includes('@g.us'));
            setContacts(prev => [...prev, ...individualContacts]);
            const pag = response.pagination;
            setTotalFromServer(pag?.total ?? null);
            setHasMore(pag?.hasMore ?? false);
            setNextOffset(pag?.nextOffset ?? nextOffset + PAGE_SIZE);
        } catch (error) {
            console.error('Error loading more contacts:', error);
            addToast('Não foi possível carregar mais contatos.', 'error');
        } finally {
            setIsLoadingMore(false);
        }
    }, [hasMore, isLoadingMore, nextOffset]);

    const fetchEmailContacts = useCallback(async (page: number = 1) => {
        const sectorId = SessionService.getSectorId();
        if (!sectorId) {
            setIsLoadingEmail(false);
            return;
        }
        try {
            setIsLoadingEmail(true);
            const result = await getEmailContactsPaged(
                sectorId,
                page,
                PAGE_SIZE,
                emailSearchTerm.trim() || undefined,
                emailArchivedFilter
            );
            setEmailContacts(result.items ?? []);
            setEmailTotalCount(result.totalCount ?? 0);
            setEmailPage(page);
        } catch (error) {
            console.error('Error fetching email contacts:', error);
            addToast('Não foi possível carregar os contatos de e-mail.', 'error');
        } finally {
            setIsLoadingEmail(false);
        }
    }, [emailSearchTerm, emailArchivedFilter]);

    useEffect(() => {
        fetchContacts();

        const handleNewMessage = (data: any) => {
            console.log('New message received in ContactsPage, refreshing list...', data);
            fetchContacts();
        };

        const handleContactUpdate = (data: any) => {
            console.log('Contact update received in ContactsPage, refreshing list...', data);
            fetchContacts();
        };

        chatWebSocketService.on('message', handleNewMessage);
        chatWebSocketService.on('contact', handleContactUpdate);

        return () => {
            chatWebSocketService.off('message', handleNewMessage);
            chatWebSocketService.off('contact', handleContactUpdate);
        };
    }, [fetchContacts]);

    useEffect(() => {
        if (activeTab === 'email') {
            fetchEmailContacts(1);
        }
    }, [activeTab, fetchEmailContacts]);

    const handleEmailSearch = () => {
        if (activeTab === 'email') fetchEmailContacts(1);
    };

    useEffect(() => {
        let filtered = contacts;

        // Filter by search term
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(c =>
                c.name?.toLowerCase().includes(term) ||
                c.number?.includes(searchTerm)
            );
        }

        // Filter by status
        if (statusFilter !== 'todos') {
            filtered = filtered.filter(c => c.contactStatus === statusFilter);
        }

        setFilteredContacts(filtered);
        setCurrentPage(0);
    }, [searchTerm, contacts, statusFilter]);

    const addToast = (message: string, type: 'success' | 'error' | 'info') => {
        setToasts(current => [...current, { id: Date.now(), message, type }]);
    };

    const removeToast = (id: number) => {
        setToasts(current => current.filter(toast => toast.id !== id));
    };

    const handleViewConversation = (contact: Contact) => {
        // Navigate to chat with this contact selected
        navigate('/chat', { state: { selectedContactId: contact.id } });
    };

    const handleImportSuccess = () => {
        fetchContacts();
        addToast('Contatos importados.', 'success');
    };

    const handleEmailImportSuccess = () => {
        fetchEmailContacts(1);
        addToast('Contatos de e-mail importados.', 'success');
    };

    const handleExportContactsForImport = async () => {
        const sid = SessionService.getSectorId();
        if (!sid) {
            addToast('Selecione uma conexão para exportar os contatos.', 'error');
            return;
        }
        try {
            setIsExportingContacts(true);
            await exportContactsForImport(sid);
            addToast('Contatos exportados no mesmo formato da importação (Nome, Telefone).', 'success');
        } catch (e: unknown) {
            addToast(apiErrorMessage(e, 'Não foi possível exportar os contatos.'), 'error');
        } finally {
            setIsExportingContacts(false);
        }
    };

    // Pagination
    const totalPages = Math.ceil(filteredContacts.length / CONTACTS_PER_PAGE);
    const startIndex = currentPage * CONTACTS_PER_PAGE;
    const paginatedContacts = filteredContacts.slice(startIndex, startIndex + CONTACTS_PER_PAGE);

    const sectorId = SessionService.getSectorId();

    const kpiTotal = totalFromServer ?? contacts.length;
    const kpiQualified = contacts.filter((c) => c.contactStatus === 'Qualificado').length;
    const kpiNew = contacts.filter((c) => !c.contactStatus || c.contactStatus === 'Novo').length;
    const kpiAi = contacts.filter((c) => Number(c.aiActive) > 0).length;

    return (
        <div className={`contacts-page-wrapper ${visionEnabled ? 'is-vision' : ''}`}>
            <div className="contacts-container">
                <div className="contacts-header">
                    <div className="contacts-header-content">
                        <h1>{visionEnabled ? 'Clientes' : 'Contatos'}</h1>
                        <p className="contacts-header-description">
                            {visionEnabled
                                ? 'Base de clientes e leads — conversas, status e o funil na mesma operação.'
                                : 'Gerencie seus contatos e abra as conversas'}
                        </p>
                        <div className="contacts-tabs">
                            <button
                                className={`contacts-tab ${activeTab === 'whatsapp' ? 'contacts-tab-active' : ''}`}
                                onClick={() => setActiveTab('whatsapp')}
                            >
                                <FiMessageSquare size={18} />
                                WhatsApp
                            </button>
                            {hasModule('email') && (
                            <button
                                className={`contacts-tab ${activeTab === 'email' ? 'contacts-tab-active' : ''}`}
                                onClick={() => setActiveTab('email')}
                            >
                                <FiMail size={18} />
                                E-mail
                            </button>
                            )}
                        </div>
                    </div>
                    <div className="contacts-header-actions">
                        {visionEnabled && (
                            <button
                                type="button"
                                className="contacts-export-btn"
                                onClick={() => navigate('/oportunidades')}
                            >
                                Ver funil
                            </button>
                        )}
                        {activeTab === 'whatsapp' && (
                            <>
                            <button
                                type="button"
                                className="contacts-export-btn"
                                onClick={handleExportContactsForImport}
                                disabled={!sectorId || isExportingContacts}
                            >
                                <FiDownload size={18} />
                                {isExportingContacts
                                    ? 'Exportando…'
                                    : 'Exportar p/ importação'}
                            </button>
                            <button
                                className="contacts-import-btn"
                                onClick={() => setShowImportModal(true)}
                                disabled={!sectorId}
                            >
                                <FiUpload size={18} />
                                {visionEnabled ? 'Importar clientes' : 'Importar contatos'}
                            </button>
                            </>
                        )}
                        {activeTab === 'email' && (
                            <button
                                className="contacts-import-btn"
                                onClick={() => setShowEmailImportModal(true)}
                                disabled={!sectorId}
                            >
                                <FiUpload size={18} />
                                Importar e-mails
                            </button>
                        )}
                    </div>
                </div>

                {visionEnabled && activeTab === 'whatsapp' && !isLoading && sectorId && (
                    <div className="mai-funil-kpis mai-funil-kpis-4 contacts-vision-kpis" aria-label="Resumo de clientes">
                        <div className="mai-funil-kpi">
                            <strong>{kpiTotal}</strong>
                            <span>Clientes cadastrados</span>
                        </div>
                        <div className="mai-funil-kpi">
                            <strong>{kpiQualified}</strong>
                            <span>Qualificados na lista</span>
                        </div>
                        <div className="mai-funil-kpi">
                            <strong>{kpiNew}</strong>
                            <span>Novos na lista</span>
                        </div>
                        <div className="mai-funil-kpi">
                            <strong>{kpiAi}</strong>
                            <span>Com IA ativa na lista</span>
                        </div>
                    </div>
                )}

                {visionEnabled && activeTab === 'whatsapp' && !isLoading && sectorId && (
                    <p className="mai-funil-banner contacts-vision-banner">
                        Clientes alimentam o funil quando há intenção comercial. Contatos sem estágio ficam aqui na base.
                        {' '}
                        <button type="button" className="mai-linkish" onClick={() => navigate('/oportunidades')}>
                            Abrir funil →
                        </button>
                    </p>
                )}

                {/* Search */}
                <div className="contacts-search-container">
                    {activeTab === 'whatsapp' && (
                        <>
                            <div className="contacts-search-wrapper">
                                <FiSearch className="contacts-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar por nome ou telefone…"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="contacts-search-input"
                                />
                                {searchTerm && (
                                    <button className="contacts-search-clear" onClick={() => setSearchTerm('')}>
                                        <FiX size={16} />
                                    </button>
                                )}
                            </div>
                            <div className="contacts-filter-controls">
                                <span className="contacts-count-badge">
                                    {visionEnabled
                                        ? (totalFromServer != null
                                            ? `Mostrando ${filteredContacts.length} de ${totalFromServer} clientes`
                                            : `${filteredContacts.length} cliente${filteredContacts.length !== 1 ? 's' : ''}`)
                                        : (totalFromServer != null
                                            ? `Mostrando ${filteredContacts.length} de ${totalFromServer} contatos`
                                            : `${filteredContacts.length} contato${filteredContacts.length !== 1 ? 's' : ''}`)}
                                </span>
                                <div className="contacts-filter-group">
                                    <select className="contacts-filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                        <option value="todos">Todos os status</option>
                                        <option value="Novo">Novos</option>
                                        <option value="Qualificado">Qualificados</option>
                                        <option value="Não qualificado">Não qualificados</option>
                                    </select>
                                </div>
                            </div>
                        </>
                    )}
                    {activeTab === 'email' && (
                        <>
                            <div className="contacts-search-wrapper">
                                <FiSearch className="contacts-search-icon" />
                                <input
                                    type="text"
                                    placeholder="Buscar por nome ou e-mail…"
                                    value={emailSearchTerm}
                                    onChange={(e) => setEmailSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleEmailSearch()}
                                    className="contacts-search-input"
                                />
                                {emailSearchTerm && (
                                    <button className="contacts-search-clear" onClick={() => setEmailSearchTerm('')}>
                                        <FiX size={16} />
                                    </button>
                                )}
                            </div>
                            <div className="contacts-filter-controls">
                                <span className="contacts-count-badge">
                                    {emailTotalCount} email contact{emailTotalCount !== 1 ? 's' : ''}
                                </span>
                                <div className="contacts-filter-group">
                                    <select
                                        className="contacts-filter-select"
                                        value={emailArchivedFilter === undefined ? 'todos' : emailArchivedFilter ? 'arquivados' : 'ativos'}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setEmailArchivedFilter(v === 'todos' ? undefined : v === 'arquivados');
                                        }}
                                    >
                                        <option value="todos">Todos</option>
                                        <option value="ativos">Ativos</option>
                                        <option value="arquivados">Arquivados</option>
                                    </select>
                                </div>
                                <button
                                    className="contacts-new-inline-btn"
                                    onClick={handleEmailSearch}
                                    style={{ padding: '6px 12px' }}
                                >
                                    <FiSearch size={14} />
                                    Buscar
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="contacts-content">
                    {activeTab === 'whatsapp' && (
                    <>
                    {isLoading && sectorId ? (
                        <div className="contacts-loading">
                            <LoadingSpinner size="lg" color="primary" />
                            <span>Carregando contatos...</span>
                        </div>
                    ) : !sectorId ? (
                        <div className="contacts-empty">
                            <h3>Nenhuma conexão selecionada</h3>
                            <p>Selecione uma conexão no menu para ver os contatos</p>
                        </div>
                    ) : filteredContacts.length === 0 ? (
                        <div className="contacts-empty">
                            <FiUser size={48} />
                            <h3>
                                {searchTerm
                                    ? (visionEnabled ? 'Nenhum cliente encontrado' : 'Nenhum contato encontrado')
                                    : (visionEnabled ? 'Ainda sem clientes' : 'Ainda sem contatos')}
                            </h3>
                            <p>
                                {searchTerm
                                    ? 'Tente outro termo de busca'
                                    : (visionEnabled ? 'Importe clientes para começar' : 'Importe contatos para começar')}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="contacts-table-container">
                                <table className="contacts-table">
                                    <thead>
                                        <tr>
                                            <th>Foto</th>
                                            <th>Nome</th>
                                            <th>Status</th>
                                            <th className="contacts-align-right">Ação</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedContacts.map((contact) => (
                                            <tr key={contact.id}>
                                                <td>
                                                    <div className="contact-avatar-container">
                                                        <img
                                                            src={processAvatarUrl(contact.avatarUrl) || '/user.png'}
                                                            alt={contact.name}
                                                            className="contact-avatar"
                                                            onError={(e) => {
                                                                const el = e.target as HTMLImageElement;
                                                                el.style.display = 'none';
                                                                el.nextElementSibling?.classList.remove('hidden');
                                                            }}
                                                        />
                                                        <div className="contact-avatar-placeholder hidden">
                                                            {(contact.name || '?').charAt(0).toUpperCase()}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="contact-name" title={contact.name || contact.number || ''}>
                                                        {(contact.name && contact.name.trim()) || contact.number || 'Sem nome'}
                                                    </span>
                                                    {contact.number &&
                                                        contact.name &&
                                                        contact.name.trim() !== contact.number.trim() && (
                                                            <span className="contact-phone-sub" title={contact.number}>
                                                                {contact.number}
                                                            </span>
                                                        )}
                                                </td>
                                                <td>
                                                    <div className="contacts-status-cell">
                                                        <div className="contact-status-indicator" style={{
                                                            width: '8px',
                                                            height: '8px',
                                                            borderRadius: '50%',
                                                            backgroundColor: contact.contactStatus === 'Qualificado' ? '#25D366' :
                                                                contact.contactStatus === 'Não qualificado' ? 'var(--error-color)' : 'var(--primary-color)',
                                                            display: 'inline-block',
                                                            marginRight: '6px'
                                                        }} />
                                                        <span className="contacts-status-label">
                                                            {contact.contactStatus || '—'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="contacts-align-right">
                                                    <div className="contacts-row-actions">
                                                        <button
                                                            className="contact-view-btn-small"
                                                            onClick={() => handleViewConversation(contact)}
                                                        >
                                                            <FiMessageSquare size={14} />
                                                            Abrir conversa
                                                        </button>
                                                        {visionEnabled && (
                                                            <button
                                                                type="button"
                                                                className="contact-view-btn-small"
                                                                onClick={() => openRegisterConversation({
                                                                    contactId: contact.id,
                                                                    contactName: contact.name,
                                                                    sourceSurface: 'cliente',
                                                                    screen: 'contacts',
                                                                    pathname: '/contacts',
                                                                    subjectKind: 'qualified_contact',
                                                                })}
                                                            >
                                                                <FiMic size={14} />
                                                                Registrar
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Load more (appends contacts from server) */}
                            {hasMore && (
                                <div className="contacts-load-more-wrap">
                                    <button
                                        type="button"
                                        className="contacts-load-more-btn"
                                        onClick={loadMoreContacts}
                                        disabled={isLoadingMore}
                                    >
                                        {isLoadingMore ? (
                                            <>
                                                <ImSpinner2 className="contacts-load-more-spinner" size={18} />
                                                Carregando…
                                            </>
                                        ) : (
                                            visionEnabled ? 'Carregar mais clientes' : 'Carregar mais contatos'
                                        )}
                                    </button>
                                </div>
                            )}

                            {/* Pagination (páginas apenas do que já está carregado na tela) */}
                            {totalPages > 1 && (
                                <div className="contacts-pagination">
                                    <span className="contacts-pagination-info">
                                        Exibindo {startIndex + 1}-{Math.min(startIndex + CONTACTS_PER_PAGE, filteredContacts.length)} de {filteredContacts.length}
                                    </span>
                                    <div className="contacts-pagination-buttons">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                                            disabled={currentPage === 0}
                                            className="contacts-pagination-btn"
                                        >
                                            Anterior
                                        </button>
                                        <span className="contacts-pagination-current">
                                            Página {currentPage + 1} de {totalPages}
                                        </span>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                                            disabled={currentPage >= totalPages - 1}
                                            className="contacts-pagination-btn"
                                        >
                                            Próxima
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    </>
                    )}
                    {activeTab === 'email' && (
                        <>
                        {isLoadingEmail && sectorId ? (
                            <div className="contacts-loading">
                                <LoadingSpinner size="lg" color="primary" />
                                <span>Carregando contatos de e-mail…</span>
                            </div>
                        ) : !sectorId ? (
                            <div className="contacts-empty">
                                <h3>Nenhuma conexão selecionada</h3>
                                <p>Selecione uma conexão no menu para ver os contatos de e-mail</p>
                            </div>
                        ) : emailContacts.length === 0 ? (
                            <div className="contacts-empty">
                                <FiMail size={48} />
                                <h3>{emailSearchTerm ? 'Nenhum contato de e-mail encontrado' : 'Ainda sem contatos de e-mail'}</h3>
                                <p>{emailSearchTerm ? 'Tente outra busca' : 'Importe contatos de e-mail para começar'}</p>
                            </div>
                        ) : (
                            <>
                                <div className="contacts-table-container">
                                    <table className="contacts-table">
                                        <thead>
                                            <tr>
                                                <th>Nome</th>
                                                <th>E-mail</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {emailContacts.map((ec) => (
                                                <tr key={ec.id}>
                                                    <td>
                                                        <span className="contact-name">{ec.name ?? '—'}</span>
                                                    </td>
                                                    <td>
                                                        <span style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>{ec.email ?? '—'}</span>
                                                    </td>
                                                    <td>
                                                        <div className="contacts-status-cell">
                                                            <div className="contact-status-indicator" style={{
                                                                width: '8px', height: '8px', borderRadius: '50%',
                                                                backgroundColor: ec.contactStatus === 'Qualificado' ? '#25D366' :
                                                                    ec.contactStatus === 'Não qualificado' ? 'var(--error-color)' : 'var(--primary-color)',
                                                                display: 'inline-block', marginRight: '6px',
                                                            }} />
                                                            <span className="contacts-status-label">{ec.contactStatus ?? '—'}</span>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {emailTotalCount > PAGE_SIZE && (
                                    <div className="contacts-pagination" style={{ marginTop: 16 }}>
                                        <span className="contacts-pagination-info">
                                            Página {emailPage} de {Math.ceil(emailTotalCount / PAGE_SIZE) || 1}
                                        </span>
                                        <div className="contacts-pagination-buttons">
                                            <button
                                                onClick={() => fetchEmailContacts(emailPage - 1)}
                                                disabled={emailPage <= 1 || isLoadingEmail}
                                                className="contacts-pagination-btn"
                                            >
                                                Anterior
                                            </button>
                                            <button
                                                onClick={() => fetchEmailContacts(emailPage + 1)}
                                                disabled={emailPage >= Math.ceil(emailTotalCount / PAGE_SIZE) || isLoadingEmail}
                                                className="contacts-pagination-btn"
                                            >
                                                Próxima
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                        </>
                    )}
                </div>
            </div>

            <ContactImportModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                onImportSuccess={handleImportSuccess}
                sectorId={sectorId || 0}
                isOfficial={isOfficial}
            />

            <EmailContactImportModal
                isOpen={showEmailImportModal}
                onClose={() => setShowEmailImportModal(false)}
                onImportSuccess={handleEmailImportSuccess}
                sectorId={sectorId || 0}
            />

            {/* Toasts */}
            <ToastContainer>
                {toasts.map(toast => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        type={toast.type}
                        onClose={() => removeToast(toast.id)}
                    />
                ))}
            </ToastContainer>
        </div>
    );
};

export default ContactsPage;
